import { useState, useCallback, useMemo } from 'react';
import {
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  FileText,
  History,
  Lock,
  RefreshCw,
  Shield,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { DataTablePagination } from '@/components/DataTablePagination';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/ui/PageHeader';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { SearchHighlight } from '@/components/ui/search-highlight';
import { classifyError } from '../../lib/errors';
import { fullDate, relativeTime } from '@/lib/datetime';
import { useExpandSet } from '@/hooks/useExpandSet';
import type { AuditoriaOut } from '../../api/auditoria';
import { useAuditoriaQuery, useActorNames } from '../../hooks/useAuditoria';
import {
  AuditoriaFilterBar,
  auditPeriodToStart,
  type AuditPeriod,
} from './components/AuditoriaFilterBar';
import { ChangeHistoryTab } from './components/ChangeHistoryTab';

/* ------------------------------------------------------------------ */
/*  Constantes                                                         */
/* ------------------------------------------------------------------ */

const AUTO_REFRESH_MS = 30_000;

const CARD_BORDER = 'border border-gray-300 dark:border-gray-700';

/* ------------------------------------------------------------------ */
/*  Badge helpers                                                      */
/* ------------------------------------------------------------------ */

const OUTCOME_STYLES: Record<string, string> = {
  success: 'bg-green-50 text-green-600 dark:bg-green-950/40 dark:text-green-400',
  failure: 'bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400',
};

const OUTCOME_BORDER: Record<string, string> = {
  success: 'border-l-green-400',
  failure: 'border-l-red-400',
};

function OutcomeBadge({ outcome }: { outcome: string }) {
  return (
    <span className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-xs font-semibold leading-none ${OUTCOME_STYLES[outcome] || 'bg-gray-100 text-gray-500'}`}>
      {outcome === 'success' ? 'Sucesso' : outcome === 'failure' ? 'Falha' : outcome}
    </span>
  );
}

const ACAO_STYLES: Record<string, string> = {
  create: 'bg-green-50 text-green-700 dark:bg-green-950/50 dark:text-green-400',
  update: 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400',
  delete: 'bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-400',
  login: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400',
  login_failed: 'bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400',
  logout: 'bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400',
  upload: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-400',
  download: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950/50 dark:text-cyan-400',
  toggle: 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400',
  share: 'bg-purple-50 text-purple-700 dark:bg-purple-950/50 dark:text-purple-400',
  sync: 'bg-teal-50 text-teal-700 dark:bg-teal-950/50 dark:text-teal-400',
};

function AcaoBadge({ acao }: { acao: string }) {
  return (
    <span className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-xs font-semibold leading-none ${ACAO_STYLES[acao] || 'bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400'}`}>
      {acao}
    </span>
  );
}

const ALVO_STYLES: Record<string, string> = {
  documento: 'bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400',
  artefato: 'bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400',
  processo: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400',
  certificado: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400',
  auth: 'bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400',
  user: 'bg-purple-50 text-purple-600 dark:bg-purple-950/40 dark:text-purple-400',
  modelo: 'bg-teal-50 text-teal-600 dark:bg-teal-950/40 dark:text-teal-400',
};

function AlvoBadge({ alvo }: { alvo: string }) {
  return (
    <span className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-xs font-semibold leading-none ${ALVO_STYLES[alvo] || 'bg-gray-100 text-gray-500 dark:bg-gray-800/50 dark:text-gray-400'}`}>
      {alvo}
    </span>
  );
}

const ACTOR_TYPE_STYLES: Record<string, string> = {
  user: 'bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400',
  admin: 'bg-purple-50 text-purple-600 dark:bg-purple-950/40 dark:text-purple-400',
  system: 'bg-gray-100 text-gray-500 dark:bg-gray-800/50 dark:text-gray-400',
  webhook: 'bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400',
  worker: 'bg-teal-50 text-teal-600 dark:bg-teal-950/40 dark:text-teal-400',
};

function ActorTypeBadge({ actorType }: { actorType: string }) {
  return (
    <span className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-xs font-semibold leading-none ${ACTOR_TYPE_STYLES[actorType] || 'bg-gray-100 text-gray-500'}`}>
      {actorType}
    </span>
  );
}

function OriginBadge({ origin }: { origin: string }) {
  return (
    <span className="inline-flex shrink-0 items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium leading-none text-gray-500 dark:bg-gray-800/50 dark:text-gray-400">
      {origin}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Componentes auxiliares                                             */
/* ------------------------------------------------------------------ */

function RelativeTimestamp({ iso }: { iso: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="shrink-0 cursor-help whitespace-nowrap font-mono text-xs text-muted-foreground">
          {relativeTime(iso)}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top"><span className="text-xs">{fullDate(iso)}</span></TooltipContent>
    </Tooltip>
  );
}

function ExpandCollapseToggle({ chevron }: { chevron: 'down' | 'right' }) {
  return chevron === 'down'
    ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
    : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />;
}

function CardLoading() {
  return (
    <div className="flex justify-center py-12">
      <Spinner size={32} label="Carregando dados" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Detail row (expanded)                                              */
/* ------------------------------------------------------------------ */

function DetailItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="text-xs">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  AuditoriaCard                                                      */
/* ------------------------------------------------------------------ */

function AuditoriaCard({
  item,
  expanded,
  onToggle,
  actorName,
  searchQuery,
  onViewChanges,
}: {
  item: AuditoriaOut;
  expanded: boolean;
  onToggle: () => void;
  actorName?: string;
  searchQuery: string;
  onViewChanges?: (requestId: string) => void;
}) {
  const borderColor = OUTCOME_BORDER[item.outcome] || 'border-l-gray-300';
  const hasMetadados = item.metadados && Object.keys(item.metadados).length > 0;

  return (
    <div className={`${CARD_BORDER} rounded-lg border-l-4 ${borderColor} bg-[var(--color-bg-paper)]`}>
      {/* Linha principal (colapsada) */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full cursor-pointer items-center gap-2 px-3 py-2.5 text-left sm:gap-3"
      >
        <RelativeTimestamp iso={item.created_at} />
        <AcaoBadge acao={item.acao} />
        <AlvoBadge alvo={item.alvo} />
        <OutcomeBadge outcome={item.outcome} />
        {actorName && (
          <span className="hidden break-all text-xs text-muted-foreground sm:inline">
            <SearchHighlight text={actorName} query={searchQuery} />
          </span>
        )}
        <span className="hidden font-mono text-xs text-muted-foreground lg:inline">
          {item.ip || '—'}
        </span>
        <span className="ml-auto">
          <ExpandCollapseToggle chevron={expanded ? 'down' : 'right'} />
        </span>
      </button>

      {/* Detalhes expandidos */}
      {expanded && (
        <div className="border-t border-[var(--color-divider)] px-3 py-3 space-y-3">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
            <DetailItem label="Tipo de ator">
              <ActorTypeBadge actorType={item.actor_type} />
            </DetailItem>
            <DetailItem label="Origem">
              <OriginBadge origin={item.origin} />
            </DetailItem>
            <DetailItem label="IP">
              <span className="font-mono text-xs">{item.ip || '—'}</span>
            </DetailItem>
            {item.actor_id && (
              <DetailItem label="Ator">
                <span className="inline-flex items-center gap-1">
                  <span className="font-mono text-xs"><SearchHighlight text={actorName || item.actor_id} query={searchQuery} /></span>
                  <CopyButton text={item.actor_id} />
                </span>
              </DetailItem>
            )}
            {item.resource_id && (
              <DetailItem label="Resource ID">
                <span className="inline-flex items-center gap-1">
                  <span className="font-mono text-xs"><SearchHighlight text={item.resource_id} query={searchQuery} /></span>
                  <CopyButton text={item.resource_id} />
                </span>
              </DetailItem>
            )}
            {item.request_id && (
              <DetailItem label="Request ID">
                <span className="inline-flex items-center gap-1">
                  <span className="font-mono text-xs"><SearchHighlight text={item.request_id} query={searchQuery} /></span>
                  <CopyButton text={item.request_id} />
                </span>
              </DetailItem>
            )}
            {item.correlation_id && (
              <DetailItem label="Correlation ID">
                <span className="inline-flex items-center gap-1">
                  <span className="font-mono text-xs"><SearchHighlight text={item.correlation_id} query={searchQuery} /></span>
                  <CopyButton text={item.correlation_id} />
                </span>
              </DetailItem>
            )}
          </div>

          {hasMetadados && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Metadados</span>
                <CopyButton text={JSON.stringify(item.metadados, null, 2)} />
              </div>
              <pre className="max-h-48 overflow-auto rounded border border-[var(--color-divider)] bg-[var(--color-bg-elevated)] p-2 font-mono text-xs">
                {JSON.stringify(item.metadados, null, 2)}
              </pre>
            </div>
          )}

          {item.request_id && onViewChanges && (
            <div className="pt-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 text-xs"
                onClick={() => onViewChanges(item.request_id!)}
              >
                <History className="h-3.5 w-3.5" />
                Ver mudanças
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab genérica                                                       */
/* ------------------------------------------------------------------ */

interface TabContentProps {
  autoRefresh: boolean;
  fixedAlvo?: string;
  fixedOutcome?: string;
  onViewChanges?: (requestId: string) => void;
}

function AuditoriaTabContent({ autoRefresh, fixedAlvo, fixedOutcome, onViewChanges }: TabContentProps) {
  const [period, setPeriod] = useState<AuditPeriod>('24h');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [alvo, setAlvo] = useState(fixedAlvo || '__all__');
  const [acao, setAcao] = useState('__all__');
  const [outcome, setOutcome] = useState(fixedOutcome || '__all__');
  const [actorType, setActorType] = useState('__all__');

  const { expandedSet, toggle, expandAll, collapseAll } = useExpandSet();

  const params = useMemo(() => {
    const p: Record<string, unknown> = {
      data_inicio: auditPeriodToStart(period),
      page,
      page_size: pageSize,
    };
    if (search.trim()) p.search = search.trim();
    const effectiveAlvo = fixedAlvo || (alvo !== '__all__' ? alvo : undefined);
    if (effectiveAlvo) p.alvo = effectiveAlvo;
    if (acao !== '__all__') p.acao = acao;
    const effectiveOutcome = fixedOutcome || (outcome !== '__all__' ? outcome : undefined);
    if (effectiveOutcome) p.outcome = effectiveOutcome;
    if (actorType !== '__all__') p.actor_type = actorType;
    return p;
  }, [period, page, pageSize, search, alvo, acao, outcome, actorType, fixedAlvo, fixedOutcome]);

  const { data, isLoading, error } = useAuditoriaQuery(
    params,
    true,
    autoRefresh ? AUTO_REFRESH_MS : false,
  );

  const actorIds = useMemo(
    () => data?.items.map((i) => i.actor_id) ?? [],
    [data],
  );
  const { data: actorNames } = useActorNames(actorIds);

  // Reset page quando filtros mudam
  const resetPage = useCallback(() => setPage(1), []);

  return (
    <div className="space-y-4 pt-4">
      <AuditoriaFilterBar
        search={search}
        onSearchChange={(v) => { setSearch(v); resetPage(); }}
        period={period}
        onPeriodChange={(v) => { setPeriod(v); resetPage(); }}
        alvo={alvo}
        onAlvoChange={(v) => { setAlvo(v); resetPage(); }}
        acao={acao}
        onAcaoChange={(v) => { setAcao(v); resetPage(); }}
        outcome={outcome}
        onOutcomeChange={(v) => { setOutcome(v); resetPage(); }}
        hideOutcome={!!fixedOutcome}
        actorType={actorType}
        onActorTypeChange={(v) => { setActorType(v); resetPage(); }}
        hideAlvo={!!fixedAlvo}
      />

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error).message}</AlertDescription>
        </Alert>
      )}

      {isLoading && <CardLoading />}

      {!isLoading && data && data.items.length === 0 && (
        <EmptyState
          icon={<Shield className="h-10 w-10" />}
          title="Nenhum registro encontrado"
          description="Ajuste os filtros ou o período para encontrar registros de auditoria."
        />
      )}

      {!isLoading && data && data.items.length > 0 && (
        <>
          {/* Barra de ações */}
          <div className="flex items-center justify-end">
            <div className="flex shrink-0 gap-1">
              <Button variant="ghost" size="sm" onClick={() => expandAll(data.items.length)} title="Expandir todos">
                <ChevronsUpDown className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={collapseAll} title="Colapsar todos">
                <ChevronsDownUp className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Lista de cards */}
          <div className="space-y-2">
            {data.items.map((item, idx) => (
              <AuditoriaCard
                key={item.id}
                item={item}
                expanded={expandedSet.has(idx)}
                onToggle={() => toggle(idx)}
                actorName={item.actor_id ? actorNames?.[item.actor_id] : undefined}
                searchQuery={search}
                onViewChanges={onViewChanges}
              />
            ))}
          </div>

          {/* Paginação */}
          <DataTablePagination
            page={data.page}
            pageSize={data.page_size}
            total={data.total}
            pages={data.pages}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); resetPage(); }}
            pageSizeOptions={[50, 100, 200]}
          />
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Página principal                                                   */
/* ------------------------------------------------------------------ */

export default function AuditoriaPage() {
  const queryClient = useQueryClient();
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('todos');
  const [changeHistoryRequestId, setChangeHistoryRequestId] = useState<string | undefined>();

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.auditoria }),
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.changeHistory }),
    ]).then(() => {
      setIsRefreshing(false);
    });
  }, [queryClient]);

  const handleViewChanges = useCallback((requestId: string) => {
    setChangeHistoryRequestId(requestId);
    setActiveTab('historico');
  }, []);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Auditoria LGPD"
        subtitle="Trilha de auditoria para conformidade com a LGPD"
        icon={<Shield className="h-6 w-6" />}
        actions={
          <>
            <label className="flex cursor-pointer items-center gap-2">
              <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh} />
              <span className="whitespace-nowrap text-sm text-muted-foreground">Auto (30s)</span>
            </label>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
              <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Atualizar</span>
            </Button>
          </>
        }
      />

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v !== 'historico') setChangeHistoryRequestId(undefined); }} className="w-full">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="todos" className="gap-1.5">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Todos</span>
          </TabsTrigger>
          <TabsTrigger value="usuarios" className="gap-1.5">
            <Lock className="h-4 w-4" />
            <span className="hidden sm:inline">Usuários</span>
          </TabsTrigger>
          <TabsTrigger value="documentos" className="gap-1.5">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Documentos</span>
          </TabsTrigger>
          <TabsTrigger value="historico" className="gap-1.5">
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">Histórico de Entidades</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="todos" forceMount className="data-[state=inactive]:hidden">
          <AuditoriaTabContent autoRefresh={autoRefresh} onViewChanges={handleViewChanges} />
        </TabsContent>
        <TabsContent value="usuarios" forceMount className="data-[state=inactive]:hidden">
          <AuditoriaTabContent autoRefresh={autoRefresh} fixedAlvo="user" onViewChanges={handleViewChanges} />
        </TabsContent>
        <TabsContent value="documentos" forceMount className="data-[state=inactive]:hidden">
          <AuditoriaTabContent autoRefresh={autoRefresh} fixedAlvo="documento" onViewChanges={handleViewChanges} />
        </TabsContent>
        <TabsContent value="historico" forceMount className="data-[state=inactive]:hidden">
          <ChangeHistoryTab
            key={changeHistoryRequestId}
            autoRefresh={autoRefresh}
            initialRequestId={changeHistoryRequestId}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
